import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import { useCallback, useMemo } from 'react';
import useAwardSocialLinks from '../../../hooks/useAwardSocialLinks';
import UsageTracker from '../../../utils/UsageTracker';
import { useTrack } from '../../tracks';
import getLang from 'I18n/utils/getLang';
import styled from 'styled-components';
import UIBox from 'UIComponents/layout/UIBox';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UICopyButton from 'UIComponents/button/UICopyButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import { FACEBOOK, FLINT, TWITTER, LINKEDIN, CALYPSO, CALYPSO_MEDIUM, CALYPSO_LIGHT, EERIE, KOALA } from 'HubStyleTokens/colors';
import { useTrackAwardProgress } from '../../userProgress';
import { useAchievementStatus } from '../achievementHooks';
const BUTTON_FONT_SIZE = 16;
const PlainLinkIconButton = styled(UIIconButton).withConfig({
  displayName: "SocialShareButtons__PlainLinkIconButton",
  componentId: "sc-1gp7cxp-0"
})(["display:flex;font-size:", "px;flex-direction:column;justify-content:center;&:hover{text-decoration:none;}"], BUTTON_FONT_SIZE);
const PlainCopyButton = styled(UICopyButton).withConfig({
  displayName: "SocialShareButtons__PlainCopyButton",
  componentId: "sc-1gp7cxp-1"
})(["display:flex;font-size:", "px;flex-direction:column;justify-content:center;&:hover{text-decoration:none;}"], BUTTON_FONT_SIZE);
const PlainButtonText = styled.div.withConfig({
  displayName: "SocialShareButtons__PlainButtonText",
  componentId: "sc-1gp7cxp-2"
})(["position:absolute;top:110px;left:0;width:100%;font-weight:normal;font-size:", "px;.disabled &{color:", ";}"], BUTTON_FONT_SIZE, FLINT);
const SocialBoxContainer = styled(UIBox).withConfig({
  displayName: "SocialShareButtons__SocialBoxContainer",
  componentId: "sc-1gp7cxp-3"
})(["border-radius:3px;margin:0 12px 12px 0;width:180px;height:180px;display:flex;align-items:stretch;position:relative;&.active{border:1px solid ", ";cursor:pointer;&:hover{border-color:", ";background:", ";}}&.disabled{border:1px solid transparent;background:", ";}.full-width{width:100%;}.icon-wrapper{width:100%;position:relative;top:-20px;}"], CALYPSO_MEDIUM, CALYPSO, CALYPSO_LIGHT, KOALA);
export const SocialBox = ({
  children,
  copyValue,
  disabled,
  href,
  icon,
  iconColor,
  onClick
}) => {
  const Button = copyValue ? PlainCopyButton : PlainLinkIconButton;
  const color = disabled ? FLINT : iconColor;
  return /*#__PURE__*/_jsx(SocialBoxContainer, {
    className: disabled ? 'disabled' : 'active',
    "data-test-id": "social-sharing-option",
    children: /*#__PURE__*/_jsxs(Button, {
      className: "p-x-3 full-width text-center",
      disabled: disabled,
      external: true,
      hint: disabled ? '' : undefined,
      href: href,
      iconProps: {
        className: 'hidden'
      },
      onClick: onClick,
      target: "_blank",
      use: "unstyled",
      value: copyValue,
      children: [/*#__PURE__*/_jsx("div", {
        className: "m-top-6 icon-wrapper",
        children: /*#__PURE__*/_jsx(UIIcon, {
          color: color,
          name: icon,
          size: "lg"
        })
      }), /*#__PURE__*/_jsx(PlainButtonText, {
        children: children
      })]
    })
  });
};

const SocialShareButtons = ({
  trackId
}) => {
  const shareLinks = useAwardSocialLinks(trackId, getLang());
  const socialShareButtonDefinitions = useMemo(() => shareLinks ? [{
    networkName: 'facebook',
    href: shareLinks.facebook,
    icon: 'socialBlockFacebook',
    color: FACEBOOK,
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.awardShareControls.facebook"
    })
  }, {
    networkName: 'linkedin',
    href: shareLinks.linkedin,
    icon: 'socialBlockLinkedin',
    color: LINKEDIN,
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.awardShareControls.linkedinPost"
    })
  }, {
    networkName: 'linkedin',
    href: shareLinks.linkedinAddToProfile,
    icon: 'socialBlockLinkedin',
    color: LINKEDIN,
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.awardShareControls.linkedinLicenses"
    })
  }, {
    networkName: 'twitter',
    href: shareLinks.twitter,
    icon: 'socialBlockTwitter',
    color: TWITTER,
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.awardShareControls.twitter"
    })
  }] : [], [shareLinks]);
  const {
    track
  } = useTrack(Number(trackId), getLang());
  const {
    award
  } = useTrackAwardProgress(Number(trackId));
  const {
    isDisabled
  } = useAchievementStatus(trackId);
  const onSocialShareClick = useCallback((socialName, destinationUrl) => {
    if (!award) return;
    UsageTracker.track('awardInteraction', {
      action: 'social-share',
      awardName: award.awardName,
      trackId: track ? track.id : null,
      trackName: track ? track.name : null,
      view: 'sharing-controls',
      socialName,
      destinationUrl
    });
  }, [award, track]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [socialShareButtonDefinitions.map(({
      networkName,
      href,
      icon,
      color,
      buttonText
    }) => /*#__PURE__*/_jsx(SocialBox, {
      disabled: isDisabled,
      href: href,
      icon: icon,
      iconColor: color,
      onClick: () => onSocialShareClick(networkName, href),
      children: buttonText
    }, `${networkName}-${href}`)), /*#__PURE__*/_jsx(SocialBox, {
      copyValue: shareLinks ? shareLinks.achievementUrl : '',
      disabled: isDisabled,
      icon: "socialBlockLink",
      iconColor: EERIE,
      onClick: () => onSocialShareClick('copy-link', shareLinks ? shareLinks.achievementUrl : ''),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.awardShareControls.copyLink"
      })
    })]
  });
};

export default SocialShareButtons;